/**
 * Device detection utility to identify iPhone devices.
 * Ensures non-iPhone devices (Windows, Mac, Linux, Android, iPad, Tablets)
 * continue receiving the standard HRMS experience without any changes.
 */
export const isIPhoneDevice = (): boolean => {
  if (typeof window === "undefined" || !navigator) {
    return false;
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || "";
  const platform = navigator.platform || "";

  // Check for explicit iPhone user agent
  const isIPhoneUA = /iPhone/i.test(userAgent);

  // Exclude iPad, Mac, Android, and Windows
  const isIPad = /iPad/i.test(userAgent) || (platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(userAgent);
  const isWindows = /Windows/i.test(userAgent);

  if (isIPhoneUA && !isIPad && !isAndroid && !isWindows) {
    return true;
  }

  return false;
};

/**
 * Detects if the current device is an iPhone running native Apple Safari browser.
 * On iOS, third-party browsers (Chrome CriOS, Firefox FxiOS, Edge EdgiOS, Brave, Opera, etc.)
 * or in-app webviews lack PWA/standalone capabilities and certain location/camera behaviors.
 */
export const isSafariOnIPhone = (): boolean => {
  if (!isIPhoneDevice()) {
    return false;
  }

  const ua = navigator.userAgent || "";

  // Third-party iOS browser user agent tokens:
  const isChromeIOS = /CriOS/i.test(ua);
  const isFirefoxIOS = /FxiOS/i.test(ua);
  const isEdgeIOS = /EdgiOS/i.test(ua);
  const isOperaIOS = /OPiOS|OPT/i.test(ua);
  const isBraveIOS = /Brave/i.test(ua);
  const isGoogleApp = /GSA/i.test(ua);
  const isInAppSocial = /FBAN|FBAV|Instagram|LinkedInApp|MicroMessenger|Line/i.test(ua);

  // Native Safari MUST have "Safari/" and "Version/" and NONE of the third-party tokens above
  const hasSafariToken = /Safari/i.test(ua);
  const hasVersionToken = /Version/i.test(ua);

  const isThirdPartyBrowser =
    isChromeIOS ||
    isFirefoxIOS ||
    isEdgeIOS ||
    isOperaIOS ||
    isBraveIOS ||
    isGoogleApp ||
    isInAppSocial;

  return hasSafariToken && hasVersionToken && !isThirdPartyBrowser;
};

